/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Assistant } from '@mui/icons-material';
import { IconButton, Tooltip, Typography, Stack } from '@mui/material';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import variants from 'modules/common/theme/variants';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import InterpreterOptionsPicker from 'modules/common/components/interpreter-picker';
import Modal from 'modules/common/components/modal';
import {
  selectAppTheme,
  selectCurrentUrl,
  selectEnableHotelGroups,
  selectEncodedTabList,
  selectHotelName,
  selectInterpreterAgentClicked,
  selectInterpreterOptions,
  selectIsDataExporting,
  selectIsDemoModeActivated,
  selectIsFirstTimeInterpreterAgentUsed,
  selectLLMErrorResponse,
  selectLLMProgressSteps,
  selectTabList,
  selectTabValue,
  selectTargetDate,
  selectTrendTargetDate,
  selectVisualAnalysis,
} from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectConsent,
  selectInterpreterAgentError,
  selectInterpreterAgentTokenUsage,
} from 'modules/users/selectors';
import { usersActions } from 'modules/users/slice';
import { ENVIRONMENT } from 'config';
import { formatDate } from 'modules/dashboard/functions';
import { DATE_TIME_PATTERN } from 'modules/common/constants/date-range';
import { differenceInCalendarDays, format } from 'date-fns';
import { selectHotelFocusOn as selectSummaryHotelFocusOn } from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import { selectHotelFocusOn as selectWorkbookHotelFocusOn } from 'modules/dashboard/components/tab-container/workbook-report/selectors';
import { selectHotelFocusOn as selectPaceHotelFocusOn } from 'modules/dashboard/components/tab-container/pace-tabs/selectors';
import { selectTrendHotelFocusOn } from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import { toStringTable } from '../mui-tabular/generate-excel';
import {
  PROCESSING_LABELS,
  buildImageExportSettings,
  createColumnSchema,
  csvToJson,
  exportVisuals,
  getCeruleanColumnSchema,
  getComponentId,
  getModelProvider,
  getTokenLimitMessage,
  noDataFilteredData,
} from './utils';

const GradientOpenWithIcon = ({ angle, disabled, loading, theme, visualId }) => (
  <>
    <svg width="0" height="0">
      <defs>
        <linearGradient id={`${visualId}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={theme?.palette?.primary?.main ?? 'navy'} />
        </linearGradient>
      </defs>
    </svg>

    <Assistant
      sx={{
        fill: disabled ? 'gray' : theme?.palette?.primary?.main ?? 'navy',
        transformBox: 'fill-box',
        transformOrigin: 'center',
        transform: `rotate(${angle}deg)`,
        transition: loading ? 'none' : 'transform 0.5s ease-in-out',

        '&:hover':
          loading || disabled
            ? {}
            : {
                transform: `rotate(${angle + 180}deg)`,
              },
        filter: disabled ? 'grayscale(100%)' : 'none',
        opacity: disabled ? 0.5 : 1,
      }}
    />
  </>
);
/**
 * Interpreter Agent Icon implementation
 * @returns
 */
const AssistantIcon = ({
  visualName = '',
  report = null,
  isProcessing = false,
  setIsProcessing = null,
  tooltipText = 'Generative AI can make mistakes. The generated analysis should always be inspected and verified before accepting results.',
  disabled = false,
  isCerulean = false,
  exportData = null,
  enableSecondaryHotelGroups = false,
  getExcelExportData = null,
  exportType = EXPORT_OPTIONS.DATA_EXPORT,
  setExportType = () => {},
  isRibbonViewEnabled = false,
}) => {
  const dispatch = useDispatch();
  const themeName = useSelector(selectAppTheme);
  const organizationId = useSelector(selectOrganizationId);
  const exportProgress = useSelector(selectLLMProgressSteps);
  const isDataExporting = useSelector(selectIsDataExporting);
  const interpreterOptions = useSelector(selectInterpreterOptions);
  const currentShareUrl = useSelector(selectCurrentUrl);
  const visualAnalysis = useSelector(selectVisualAnalysis);
  const tabValue = useSelector(selectTabValue);
  const interpreterAgentTokenUsage = useSelector(selectInterpreterAgentTokenUsage);
  const consent = useSelector(selectConsent);
  const currentUser = useSelector(selectAuthUser);
  const isFirstTimeInterpreterAgentUsed = useSelector(selectIsFirstTimeInterpreterAgentUsed);
  const InterpreterAgentError = useSelector(selectInterpreterAgentError);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const currentHotel = useSelector(selectHotelName);
  const errorData = useSelector(selectLLMErrorResponse);
  const interpreterAgentClicked = useSelector(selectInterpreterAgentClicked);
  const targetDate = useSelector(selectTargetDate);
  const trendTargetDate = useSelector(selectTrendTargetDate);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const summaryHotelFocusOn = useSelector(selectSummaryHotelFocusOn);
  const workbookHotelFocusOn = useSelector(selectWorkbookHotelFocusOn);
  const trendHotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const paceHotelFocusOn = useSelector(selectPaceHotelFocusOn);
  const TAB_VALUE = useSelector(selectTabList);
  const encodedTabs = useSelector(selectEncodedTabList);

  const [angle, setAngle] = useState(0);
  const [currentStepLabel, setCurrentStepLabel] = useState('');
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [labelIndex, setLabelIndex] = useState(0);
  const [isAgentDisabled, setAgentDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState(tooltipText);
  const [dates, setDates] = useState(targetDate);
  const [selectedTabHotelFocusOn, setSelectedTabFocusOn] = useState(null);

  const directionRef = useRef(1);
  const intervalRef = useRef(null);

  const selectedVariant = variants.find((variant) => variant.name === themeName);
  const isInterpreterAgentEnabled =
    ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED.toLowerCase() === 'true';
  const isDevModeOff = ENVIRONMENT.IS_DATA_EXPORT_INTERPRETER_ENABLED.toLowerCase() === 'true';
  const componentId = getComponentId(visualName);
  const isVisualExport =
    exportType === EXPORT_OPTIONS.VISUAL || exportType === EXPORT_OPTIONS.VISUAL_DATA;
  const isIconDisabled =
    disabled || isAgentDisabled || enableHotelGroups || enableSecondaryHotelGroups;

  const setAgentMessage = (message = '') => {
    setAgentDisabled(!!message);
    dispatch(usersActions.setInterpreterAgentMessage(message));
    setErrorMessage(message || tooltipText);
  };

  useEffect(() => {
    if (summaryHotelFocusOn && workbookHotelFocusOn && paceHotelFocusOn && trendHotelFocusOn) {
      switch (tabValue) {
        case TAB_VALUE.SUMMARY:
          setSelectedTabFocusOn(summaryHotelFocusOn);
          break;
        case TAB_VALUE.WORKBOOK_REPORT:
          setSelectedTabFocusOn(workbookHotelFocusOn);
          break;
        case TAB_VALUE.TREND_TIME:
        case TAB_VALUE.MUI_TREND_TIME:
          setSelectedTabFocusOn(trendHotelFocusOn);
          break;
        case TAB_VALUE.PACE:
        case TAB_VALUE.PACE_TEST:
          setSelectedTabFocusOn(paceHotelFocusOn);
          break;
        default:
          break;
      }
    }
  }, [summaryHotelFocusOn, workbookHotelFocusOn, paceHotelFocusOn, trendHotelFocusOn, tabValue]);

  useEffect(() => {
    switch (tabValue) {
      case TAB_VALUE.SUMMARY:
      case TAB_VALUE.WORKBOOK_REPORT:
      case TAB_VALUE.PACE:
      case TAB_VALUE.PACE_TEST:
      case TAB_VALUE.REGION_MAP:
        setDates(targetDate);
        break;
      case TAB_VALUE.TREND_TIME:
      case TAB_VALUE.MUI_TREND_TIME:
        setDates(trendTargetDate);
        break;
      default:
        break;
    }
  }, [tabValue, targetDate, trendTargetDate]);

  useEffect(() => {
    if (dates) {
      const diff = differenceInCalendarDays(new Date(dates.endDate), new Date(dates.startDate));
      setAgentMessage(diff > 400 ? 'This target date range is not yet supported.' : '');
    }
  }, [dates]);

  useEffect(() => {
    if (interpreterAgentTokenUsage) {
      setAgentMessage(getTokenLimitMessage(interpreterAgentTokenUsage));
    }
  }, [interpreterAgentTokenUsage]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (exportType === EXPORT_OPTIONS.DATA_EXPORT || exportType !== EXPORT_OPTIONS.EXCEL_EXPORT) {
      let interval;

      if (isProcessing) {
        setLabelIndex(0);
        interval = setInterval(() => {
          setLabelIndex((prevIndex) => Math.min(prevIndex + 1, PROCESSING_LABELS.length - 1));
        }, 5000);
      } else {
        setLabelIndex(0);
      }
      return () => clearInterval(interval);
    }
  }, [isProcessing]);

  useEffect(() => {
    if (
      (exportType !== EXPORT_OPTIONS.DATA_EXPORT || exportType !== EXPORT_OPTIONS.EXCEL_EXPORT) &&
      exportProgress[componentId] &&
      exportProgress[componentId]?.length !== 0 &&
      isProcessing &&
      report
    ) {
      if (exportProgress[componentId]?.length > 1) {
        exportProgress[componentId]?.slice(1).forEach((prog) => {
          dispatch(
            dashboardActions.updateNotification({
              organizationId,
              userId: prog?.userId,
              notificationId: prog?.id,
              isOpen: true,
            })
          );
        });
      }

      setCurrentStepLabel(exportProgress[componentId]?.[0]?.payload?.response);
    } else {
      setCurrentStepLabel('');
    }
  }, [exportProgress[componentId]]);

  const startPingPong = () => {
    intervalRef.current = setInterval(() => {
      setAngle(directionRef.current * 110);
      directionRef.current *= -1;
    }, 400);
  };

  const stopPingPong = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setAngle(0);
  };

  useEffect(() => {
    clearInterval(intervalRef.current);
  }, []);

  const handleCaptureClick = async () => {
    try {
      if (isProcessing) return;

      if (!isFirstTimeInterpreterAgentUsed) {
        dispatch(dashboardActions.setIsFirstTimeInterpreterAgentUsed(true));
      }

      setIsProcessing(true);
      startPingPong();

      const hash = encodedTabs[tabValue];
      const requestedTime = format(new Date(), 'yyyy-MM-dd HH:mm:ss').toString();

      let dataExport = {
        modelName: interpreterOptions?.modelName,
        modelProvider: getModelProvider(interpreterOptions?.modelName),
        visualName,
        url: `${currentShareUrl}#${hash}`,
        hotel: currentHotel?.id,
        isCerulean,
        isDemoModeOn: isDemoModeActivated,
        userId: currentUser?.id,
        title: `${visualName} : ${
          isDemoModeActivated ? currentHotel?.altName : currentHotel?.name ?? ''
        }`,
        requestedTime,
        id: componentId,
        ...(tabValue !== TAB_VALUE.REGION_MAP && {
          hotelFocusOn: selectedTabHotelFocusOn?.id,
        }),
        isDevModeOff,
      };

      if (isCerulean) {
        const schema = getCeruleanColumnSchema(visualName);
        if (exportType === EXPORT_OPTIONS.EXCEL_EXPORT) {
          const data = toStringTable(getExcelExportData());
          dataExport.isCerulean = false;
          dispatch(
            dashboardActions.exportVisualData({
              visuals: [data, { schema }],
              ...dataExport,
              organizationId,
            })
          );
        } else {
          dispatch(
            dashboardActions.exportVisualData({
              organizationId,
              columns: exportData?.columns,
              filters: exportData?.filters,
              columnSchema: schema,
              ...dataExport,
            })
          );
        }
      } else {
        const capturedState = await report.bookmarksManager.capture();
        const stateString = capturedState.state;
        const pages = await report.getPages();
        const activePage = pages.find((p) => p.isActive);
        const activePageName = activePage.name;
        const exportResults = await exportVisuals(await activePage.getVisuals());

        const structuredVisuals = exportResults.map((v) => ({
          visualName: v.visualName,
          visualType: v.visualType,
          title: v.visualTitle,
          rows: csvToJson(v.data, v.visualType),
          schema: createColumnSchema(v.data, v.pageName, visualName),
        }));
        dataExport.visualName = activePage?.displayName ?? visualName;

        switch (exportType) {
          case EXPORT_OPTIONS.DATA_EXPORT:
            dispatch(
              dashboardActions.exportVisualData({
                visuals: noDataFilteredData(structuredVisuals),
                ...dataExport,
                organizationId,
              })
            );
            break;
          case EXPORT_OPTIONS.VISUAL:
          case EXPORT_OPTIONS.VISUAL_DATA:
            if (exportType === EXPORT_OPTIONS.VISUAL_DATA) {
              dataExport = {
                visuals: noDataFilteredData(structuredVisuals),
                ...dataExport,
              };
            }
            dispatch(
              dashboardActions.exportVisualImage({
                settings: buildImageExportSettings(stateString, activePageName),
                organizationId,
                query: 'isLLMScan=true',
                reportId: report.config.id,
                groupId: report.config.groupId,
                isWorkbookHotelComparisonEnable: false,
                labels: {
                  label1: `Selection Hotel 1`,
                  label2: `Comparison Hotel 2`,
                },
                dataExport,
              })
            );
            break;
          case EXPORT_OPTIONS.EXCEL_EXPORT: {
            const schema =
              structuredVisuals
                ?.map((sch) => sch.schema)
                ?.filter((schem) => schem && Object.keys(schem).length > 0) ?? {};
            const data = await getExcelExportData();
            dispatch(
              dashboardActions.exportVisualData({
                visuals: [data, { schema }],
                ...dataExport,
                organizationId,
              })
            );
            break;
          }
          default:
            break;
        }
      }
    } catch (error) {
      stopPingPong();
      setIsProcessing(false);
    }
  };
  useEffect(() => {
    if (
      isProcessing &&
      isDataExporting &&
      isDataExporting[componentId] === false &&
      (visualAnalysis?.results?.length > 0 || InterpreterAgentError)
    ) {
      stopPingPong();
      setIsProcessing(false);
      setLabelIndex(0);
    }
  }, [isDataExporting]);
  useEffect(() => {
    if (isFocus && consent?.isFirstTime) {
      setIsFirstTime(true);
    } else if (isFocus) {
      setIsFirstTime(false);
      handleCaptureClick();
      setIsFocus(false);
    } else {
      setIsFirstTime(false);
    }
  }, [consent, isFocus]);
  useEffect(() => {
    if (
      isProcessing &&
      isDataExporting &&
      isDataExporting[componentId] === false &&
      visualAnalysis?.results?.length > 0 &&
      isFirstTimeInterpreterAgentUsed &&
      !interpreterAgentClicked
    ) {
      if (isVisualExport) {
        if (exportProgress[componentId] && exportProgress[componentId]?.length > 0) {
          dispatch(
            dashboardActions.updateNotification({
              organizationId,
              userId: exportProgress[componentId]?.[0]?.userId,
              notificationId: exportProgress[componentId]?.[0]?.id,
              isOpen: true,
            })
          );
        }
        if (errorData?.[0]?.payload?.id === componentId && errorData?.length > 0) {
          dispatch(
            dashboardActions.updateNotification({
              organizationId,
              userId: errorData?.[0]?.userId,
              notificationId: errorData?.[0]?.id,
              isOpen: true,
            })
          );
        }
      }
    }
  }, [isDataExporting]);
  const isDataAvailable =
    (isInterpreterAgentEnabled && isCerulean && exportData) ||
    (isInterpreterAgentEnabled && !isCerulean && report);
  return (
    isDataAvailable && (
      <>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {isProcessing && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              {isVisualExport ? currentStepLabel : PROCESSING_LABELS[labelIndex]}
            </Typography>
          )}
          <Tooltip title={errorMessage} placement="top">
            <span>
              <IconButton
                disabled={isIconDisabled || isDataExporting[componentId]}
                color="inherit"
                size="small"
                onClick={() => {
                  setIsFocus(true);
                  dispatch(dashboardActions.setInterpreterAgentClicked(true));
                }}
                sx={{
                  '& .MuiSvgIcon-root': {
                    width: '1.5rem',
                    height: '1.25rem',
                  },
                  '& .Mui-disabled': {
                    color: '#9e9e9e',
                    background: 'transparent',
                  },
                }}
              >
                <GradientOpenWithIcon
                  angle={angle}
                  disabled={isIconDisabled}
                  loading={isProcessing}
                  theme={selectedVariant}
                  visualId={
                    isCerulean
                      ? `${visualName?.toLowerCase()?.replaceAll(' ', '-')}`
                      : `${report?.config?.id}`
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
          {ENVIRONMENT.IS_DATA_EXPORT_INTERPRETER_ENABLED.toLowerCase() === 'false' && (
            <InterpreterOptionsPicker
              isCerulean={isCerulean}
              isExcelExportEnabled={!!getExcelExportData}
              exportType={exportType}
              setExportType={setExportType}
              isRibbonViewEnabled={isRibbonViewEnabled}
            />
          )}
        </Stack>
        <Modal
          open={isFirstTime}
          handleClose={() => {
            setIsFocus(false);
            setIsFirstTime(false);
            dispatch(dashboardActions.setInterpreterAgentClicked(false));
          }}
          title="AI-Powered Features"
          content="This platform offers functionality that leverages generative AI to support data interpretation and other operations.
Please note that AI-generated results may not always be accurate or complete; we recommend reviewing the results before using them for analysis or decision-making.
By clicking “Accept”, you acknowledge and agree to these considerations in the use of such features on this platform."
          handleSuccess={() =>
            dispatch(
              usersActions.updateUserConfiguration({
                organizationId,
                userId: currentUser?.id,
                consent: {
                  isFirstTime: false,
                  updatedTime: formatDate(new Date(), DATE_TIME_PATTERN),
                },
              })
            )
          }
          closeLabel="Cancel"
          successLabel="Accept"
          variant="contained"
          color="primary"
        />
      </>
    )
  );
};
//
export default AssistantIcon;
